import { DashboardState, ESdChangeReason } from "@kuika/kuika-cl-model";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import {
  DocumentParameterActionEditor,
  isEditingDocumentParameterSourceAction
} from "./document-parameter-action-editor";
import "./document-parameters-canvas.scss";
import { DocumentParametersSection } from "./document-parameters-section";

export interface IDocumentParametersCanvasProps {}

export interface IDocumentParametersCanvasState {}

/**
 * Canvas surface for DMS document parameters (KOA-17254) — the table saying where each `<TAG>` used in templates
 * and documents gets its value.
 *
 * <b>Why this is a top-level tool rather than a settings tab.</b> It first shipped as a tab inside Project
 * Settings, because that is where the data lives (`KProjectSettings.documentParameters`). That reasoning was
 * wrong: where a setting is stored says nothing about how often someone edits it. These definitions are authored
 * and revised repeatedly while building document flows, and the Project Settings entry sat behind an opt-in
 * feature gate — so the screen was effectively unreachable. Storage stayed put; only the entry point moved.
 *
 * <b>What is here and what is in the right panel.</b> The canvas is the parameter table and nothing else. The
 * declared document inputs and the source actions — everything a parameter's value can be built from — are
 * authored in the right panel, next to the action parameters that reference them.
 */
class DocumentParametersCanvas extends PureSdComponent<
  IDocumentParametersCanvasProps & IWithSDProps,
  IDocumentParametersCanvasState
> {
  componentDidMount = () => {
    // Action definitions feed the right panel's picker and the symbol picker's action-result options; they are not
    // loaded on every surface. Deliberately not awaited — the tool is entered from the sources tree, which has
    // already populated them, so this is a refresh behind an already-usable picker rather than a gate in front of an
    // empty one. That is also how the toolbar tools do it: `handleRedirect` never awaits its `extraSetup`.
    //
    // `skipEagerTableFetch` is what keeps it cheap. Without it this re-fetches every database datasource's schema and
    // then calls the documented multi-MB `refreshTables` (KOA-17363) — work the panel that opened this tool has
    // already done to draw itself.
    this.props.sd.loadActionDefinitions?.(true, true);
    this.props.sd.loadDocumentParameters?.(this.props.sd?.project?.id ?? "");
  };

  render() {
    if (this.props.sd?.dashboardState !== DashboardState.documentParameters) return null;

    // Authoring a source action takes the whole surface: it is a full task with its own save and its own
    // unsaved-changes guard, and two savable forms on one surface is what this tool has already been rebuilt to
    // avoid. Without this branch the left panel's "New Select Action" led nowhere at all — see the editor's own
    // comment for why that path has no canvas of its own.
    if (isEditingDocumentParameterSourceAction(this.props.sd)) return <DocumentParameterActionEditor />;

    return (
      <div className="kuika-document-parameters-canvas">
        <div className="kuika-document-parameters-canvas__card">
          <DocumentParametersSection projectId={this.props.sd?.project?.id ?? ""} />
        </div>
      </div>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const documentParametersCanvas = withSD<IDocumentParametersCanvasProps>(DocumentParametersCanvas);
export { documentParametersCanvas as DocumentParametersCanvas };
